import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { founder, salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { btnPrimaryLg } from '../../ui/styles';
import { closingHeading, heroTitle, quietLink } from '../../ui/sections';
import { ImageSlot } from '../shared/ImageSlot';
import { articleImage } from '../../data/placeholderImages';
import { MediaView } from '../shared/MediaView';
import { useCatalog } from '../../contexts/CatalogContext';
import { NotFound } from '../layout/NotFound';
import { ArticleBlock } from './ArticleBlock';
import { MentionedProducts } from './MentionedProducts';
import { articleNextStep, articlePath, relatedArticles } from './journalRules';

/** Eén journalartikel: kop, tekst, genoemde producten ernaast en "Lees ook". */
export function Article() {
  const {
    slug = ''
  } = useParams();
  const {
    articles,
    articleBySlug,
    productsByIds
  } = useCatalog();
  const article = articleBySlug(slug);
  const navigate = useNavigate();
  // Uit het journal gekomen (met of zonder categorie): één stap terug, met dezelfde keuze en scrollplek.
  const {
    state
  } = useLocation() as {
    state: {
      fromJournal?: string;
    } | null;
  };
  const fromJournal = state?.fromJournal?.startsWith('/journal') ? state.fromJournal : null;
  if (!article) {
    return <NotFound title="Dit artikel bestaat niet (meer)" body="Misschien heeft het een nieuwe titel gekregen. Alle artikelen staan in het journal." backTo={{
      to: '/journal',
      label: 'Naar alle artikelen'
    }} data-id="element-7793" data-mp="1xjqp46kug199" />;
  }
  const mentioned = productsByIds(article.productIds ?? []);
  const next = articleNextStep(mentioned[0]);
  const related = relatedArticles(articles, article);
  return <>
      <article className="bg-sand" data-id="element-7794" data-mp="1ero6ipon6awd">
        <header className="mx-auto max-w-content px-6 pb-10 pt-10 lg:px-10 lg:pt-14" data-id="element-7795" data-mp="1eqmqs7wol08b">
          <Link to={fromJournal ?? '/journal'} onClick={e => {
          if (!fromJournal) return;
          e.preventDefault();
          navigate(-1);
        }} className="-ml-1 inline-flex min-h-[44px] items-center gap-2 px-1 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7796" data-mp="1ishsvemfrhn5">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7797" data-mp="2qwzwap75vja6" />
            <span data-id="element-7798" data-mp="235koigwm89rj">
              <span className="sr-only" data-id="element-7799" data-mp="2375ia4b3vooc">Terug naar het </span>Journal
            </span>
          </Link>
          <p className="mt-10 text-[14px] text-clay" data-id="element-7800" data-mp="1y8u7nrczla52">
            {article.category} · {article.readTime} lezen · {article.date}
          </p>
          <h1 className={`mt-4 max-w-3xl ${heroTitle}`} data-id="element-7801" data-mp="285gswh98637m">{article.title}</h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-muted" data-id="element-7802" data-mp="287jrvqscv6oh">{article.excerpt}</p>
          <div className="mt-8 flex items-center gap-3" data-id="element-7803" data-mp="3noah77utoxvh">
            <img src={founder.portrait} alt="" width={44} height={44} className="h-11 w-11 shrink-0 rounded-full object-cover object-top" data-id="element-7804" data-mp="3jjkk5fofhd8z" />
            <p className="text-[14px] leading-snug text-muted" data-id="element-7805" data-mp="3r67wai9ifn60">
              <span className="block text-ink" data-id="element-7806" data-mp="3w49ihc28g2lp">
                <span className="sr-only" data-id="element-7807" data-mp="3w0vey0hu1nco">Geschreven door </span>
                {founder.name}
              </span>
              {founder.role}
            </p>
          </div>
        </header>

        {/* Het artikelbeeld over de volle breedte; zonder eigen beeld een textuur per categorie (docs/BEELDEN.md, A1).
            Het ene moment: het beeld beweegt licht binnen zijn kader. Sfeerbeeld: de titel staat er al boven. */}
        <ImageSlot code="A1" parallax priority media={{
        image: articleImage(article),
        alt: ''
      }} sizes="100vw" className="aspect-[4/3] w-full sm:aspect-[21/9]" data-id="element-7808" data-mp="3zyukomrj3qs" />

        <div className="mx-auto grid max-w-content gap-14 px-6 pb-24 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7809" data-mp="wnb7s8719z7f">
          <div className="max-w-[680px] lg:col-span-7" data-id="element-7810" data-mp="11g48mkzdsne8">
            {article.body.map((block, i) =>
          // Blokken hebben (nog) geen eigen id; de volgorde is vast zolang je leest.
          <ArticleBlock key={i} block={block} data-id="element-7811" data-mp="34v7xuibuwoy0" data-mpi={i} />)}

            {/* Wat doe ik ermee? De hoofdknop gaat naar het eerste genoemde product; de vraag aan Sarah is een rustige link. */}
            <div className="mt-14 border-t border-line pt-8" data-id="element-7812" data-mp="2v066rgy0tcss">
              <Link to={next.to} className={btnPrimaryLg} data-id="element-7813" data-mp="3smn1cefddcau">
                {next.label}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7814" data-mp="3nmqwkcyulgu8" />
              </Link>
              <p className="mt-5 text-[15px] leading-relaxed text-muted" data-id="element-7815" data-mp="3ivsf3a24icpe">Twijfel je of dit voor je huid geldt? App me gerust, ik antwoord meestal dezelfde dag.</p>
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-7816" data-mp="3dvms476stedn">
                <MessageCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7817" data-mp="agqvc9d4l507" />
                App {founder.firstName}
                <span className="sr-only" data-id="element-7818" data-mp="iqpuhrv82pbb">{shopLabels.viaWhatsApp}</span>
              </a>
            </div>
          </div>

          {mentioned.length > 0 && <aside className="lg:col-span-4 lg:col-start-9" aria-labelledby="genoemd" data-id="element-7819" data-mp="rqcjtfaog4o0">
              <div className="bg-shell p-6 lg:sticky lg:top-32" data-id="element-7820" data-mp="wp62dm4tmlv1">
                <h2 id="genoemd" className="type-name text-ink" data-id="element-7821" data-mp="wqh4lhimblk1">
                  Genoemd in dit artikel
                </h2>
                <div className="mt-1" data-id="element-7822" data-mp="11mfvbbrf5fnm">
                  <MentionedProducts products={mentioned} variant="sidebar" data-id="element-7823" data-mp="15osrb8gd5z77" />
                </div>
              </div>
            </aside>}
        </div>
      </article>

      {related.length > 0 && <section className="border-t border-line bg-shell py-16 lg:py-20" aria-labelledby="lees-ook" data-id="element-7824" data-mp="1fecbchftbz3n">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7825" data-mp="1kg1mdx31ccrq">
            <h2 id="lees-ook" className={`${closingHeading} text-ink`} data-id="element-7826" data-mp="1pdbly5k524ta">
              Lees ook
            </h2>
            <ul className="mt-8 grid gap-x-8 gap-y-12 md:grid-cols-2" data-id="element-7827" data-mp="zowgtlkvajac">
              {related.map(a => <li key={a.slug} data-id="element-7828" data-mp="urntz7orxr2r">
                  <Link to={articlePath(a)} className="group block" data-id="element-7829" data-mp="uoi5nunyyt83">
                    {/* Zelfde vorm als het raster in het journal: beeld, categorie, titel. */}
                    <MediaView media={{
                image: articleImage(a),
                alt: ''
              }} sizes="(min-width: 768px) 45vw, 100vw" className="aspect-[3/2] w-full" data-id="element-7830" data-mp="14nr5331lt01u" />
                    <p className="mt-5 text-[13px] text-clay" data-id="element-7831" data-mp="1ii0d7epgza8n">
                      {a.category} · {a.readTime}
                    </p>
                    <h3 className="mt-2 type-title text-ink transition-colors duration-150 ease-soft group-hover:text-clay" data-id="element-7832" data-mp="1semxo2dvvza5">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-7833" data-mp="3u5gj36ajzaq2">{a.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-[14px] text-ink" aria-hidden="true" data-id="element-7834" data-mp="3p6wrap9a34ps">
                      Lees verder
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} data-id="element-7835" data-mp="24p4dqjwc558o" />
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}
    </>;
}